export type AssistantAction =
  | { id: "navigate"; args: { path: string } }
  | { id: "filterPipeline"; args: Record<string, unknown> }
  | { id: "evaluate"; args: { url?: string; title?: string; subtitle?: string } }
  | { id: "evaluateCompany"; args: { company?: string } }
  | { id: "research"; args: { target?: string; title?: string } }
  | { id: "generatePdf"; args: { n?: string } }
  | { id: "setStatus"; args: { n?: string; status?: string } }
  | { id: "apply"; args: { url?: string } }
  | { id: "setApplyField"; args: { field?: string; value?: string } }
  | { id: "remember"; args: { fact?: string } }
  | { id: "setProfile"; args: Record<string, unknown> }
  | { id: "setPortals"; args: { roles?: string[] } }
  | { id: string; args: Record<string, unknown> };

const ACT_RE = /<<act:([a-zA-Z0-9_]+)\s*(\{[\s\S]*?\})?\s*>>/g;
const GO_RE = /<<go:([^>\s]+)>>/g;
const REMEMBER_RE = /<<remember:([^>]+)>>/g;

/** Actions that mutate state or spend tokens — confirm before execute. */
export const CONFIRM_ACTIONS = new Set([
  "evaluate",
  "evaluateCompany",
  "research",
  "generatePdf",
  "setStatus",
  "setProfile",
  "setPortals",
]);

/** Route-changing actions — show as clickable links; never auto-navigate. */
export const LINK_ACTIONS = new Set([
  "navigate",
  "filterPipeline",
  "apply",
]);

export function parseActionEnvelopes(text: string): AssistantAction[] {
  const actions: AssistantAction[] = [];

  const actRe = new RegExp(ACT_RE.source, "g");
  let match: RegExpExecArray | null;
  while ((match = actRe.exec(text)) !== null) {
    const id = match[1];
    let args: Record<string, unknown> = {};
    if (match[2]) {
      try {
        args = JSON.parse(match[2]) as Record<string, unknown>;
      } catch {
        // skip malformed JSON
        continue;
      }
    }
    actions.push({ id, args } as AssistantAction);
  }

  const goRe = new RegExp(GO_RE.source, "g");
  while ((match = goRe.exec(text)) !== null) {
    actions.push({ id: "navigate", args: { path: match[1] } });
  }

  const remRe = new RegExp(REMEMBER_RE.source, "g");
  while ((match = remRe.exec(text)) !== null) {
    actions.push({ id: "remember", args: { fact: match[1].trim() } });
  }

  return actions;
}

export function stripActionEnvelopes(text: string): string {
  return text
    .replace(/<<act:[a-zA-Z0-9_]+\s*\{[\s\S]*?\}>>/g, "")
    .replace(/<<act:[a-zA-Z0-9_]+\s*>>/g, "")
    .replace(/<<go:[^>\s]+>>/g, "")
    .replace(/<<remember:[^>]+>>/g, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

export function actionLabel(action: AssistantAction): string {
  switch (action.id) {
    case "navigate":
      return `Go to ${String(action.args.path ?? "/")}`;
    case "evaluate":
      return (
        (typeof action.args.title === "string" && action.args.title) ||
        "Evaluate this role"
      );
    case "evaluateCompany":
      return `Evaluate jobs at ${String(action.args.company ?? "company")}`;
    case "research":
      return (
        (typeof action.args.title === "string" && action.args.title) ||
        `Research ${String(action.args.target ?? "")}`
      );
    case "generatePdf":
      return `Generate PDF for #${String(action.args.n ?? "?")}`;
    case "setStatus":
      return `Set #${String(action.args.n)} → ${String(action.args.status)}`;
    case "apply":
      return "Open apply form";
    case "remember":
      return "Remember this";
    case "setProfile":
      return "Update profile";
    case "setPortals":
      return "Update portal filters";
    case "filterPipeline":
      return "Filter pipeline";
    case "setApplyField":
      return `Fill “${String(action.args.field ?? "field")}”`;
    default:
      return action.id;
  }
}
